/**
 * Sanity Client Configuration
 *
 * Initializes the Sanity client for fetching guidance content.
 * Uses environment variables for project configuration.
 */

import { createClient, type SanityClient } from '@sanity/client';
import imageUrlBuilder from '@sanity/image-url';
import type { SanityImageSource } from '@sanity/image-url/lib/types/types';

// Sanity configuration from environment variables
const projectId = import.meta.env.VITE_SANITY_PROJECT_ID;
const dataset = import.meta.env.VITE_SANITY_DATASET || 'production';
const apiVersion = import.meta.env.VITE_SANITY_API_VERSION || '2024-01-01';

// Check if Sanity is configured
export const isSanityConfigured = Boolean(projectId);

// Create the Sanity client
export const sanityClient: SanityClient = createClient({
  projectId: projectId || 'placeholder',
  dataset,
  apiVersion,
  useCdn: true, // Use CDN for faster reads (set to false for real-time data)
  // No token needed for public content
});

// Image URL builder for Sanity images
const builder = imageUrlBuilder(sanityClient);

/**
 * Generate image URL from Sanity image reference
 */
export function urlFor(source: SanityImageSource) {
  return builder.image(source);
}

// =============================================================================
// GROQ Queries
// =============================================================================

/**
 * Query for all guidance in a category
 */
export const guidanceListQuery = `
  *[_type == "guidance" && category == $category && isPublished == true] | order(order asc) {
    _id,
    title,
    "slug": slug.current,
    topic,
    summary,
    icon,
    order
  }
`;

/**
 * Query for single guidance by topic
 */
export const guidanceByTopicQuery = `
  *[_type == "guidance" && topic == $topic && isPublished == true][0] {
    _id,
    title,
    "slug": slug.current,
    topic,
    category,
    summary,
    icon,
    content,
    quickActions,
    "relatedGuidance": relatedGuidance[]-> {
      _id,
      title,
      "slug": slug.current,
      topic,
      summary
    }
  }
`;

/**
 * Query for all guidance (all categories)
 */
export const allGuidanceQuery = `
  *[_type == "guidance" && isPublished == true] | order(category asc, order asc) {
    _id,
    title,
    "slug": slug.current,
    topic,
    category,
    summary,
    icon,
    content,
    quickActions
  }
`;

/**
 * Query for guidance grouped by category
 */
export const guidanceByCategoryQuery = `
  {
    "values": *[_type == "guidance" && category == "values" && isPublished == true] | order(order asc) {
      _id,
      title,
      "slug": slug.current,
      topic,
      summary,
      icon,
      content,
      quickActions
    },
    "habits": *[_type == "guidance" && category == "habits" && isPublished == true] | order(order asc) {
      _id,
      title,
      "slug": slug.current,
      topic,
      summary,
      icon,
      content,
      quickActions
    },
    "routines": *[_type == "guidance" && category == "routines" && isPublished == true] | order(order asc) {
      _id,
      title,
      "slug": slug.current,
      topic,
      summary,
      icon,
      content,
      quickActions
    },
    "review": *[_type == "guidance" && category == "review" && isPublished == true] | order(order asc) {
      _id,
      title,
      "slug": slug.current,
      topic,
      summary,
      icon,
      content,
      quickActions
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface SanityGuidance {
  _id: string;
  title: string;
  slug: string;
  topic: string;
  category: string;
  summary?: string;
  icon?: string;
  content?: SanityBlock[];
  quickActions?: SanityAppLink[];
  relatedGuidance?: SanityGuidanceRef[];
}

export interface SanityGuidanceRef {
  _id: string;
  title: string;
  slug: string;
  topic: string;
  summary?: string;
}

export interface SanityAppLink {
  _key: string;
  label: string;
  route: string;
  style?: 'primary' | 'secondary' | 'ghost';
  icon?: string;
}

export interface SanityCallout {
  _key: string;
  _type: 'callout';
  type: 'tip' | 'warning' | 'info' | 'success' | 'example';
  title?: string;
  content: SanityBlock[];
}

export interface SanityInternalLink {
  _type: 'internalLink';
  route: string;
}

export interface SanityExternalLink {
  _type: 'link';
  href: string;
  openInNewTab?: boolean;
}

// Portable Text block type
export interface SanityBlock {
  _key: string;
  _type: string;
  children?: Array<{
    _key: string;
    _type: string;
    marks?: string[];
    text?: string;
  }>;
  markDefs?: Array<{
    _key: string;
    _type: string;
    href?: string;
    route?: string;
    openInNewTab?: boolean;
  }>;
  style?: string;
  listItem?: string;
  level?: number;
}

// =============================================================================
// Fetch Functions
// =============================================================================

/**
 * Fetch all guidance for a category
 */
export async function fetchGuidanceList(category: string): Promise<SanityGuidance[]> {
  if (!isSanityConfigured) {
    console.warn('Sanity not configured, returning empty array');
    return [];
  }
  return sanityClient.fetch(guidanceListQuery, { category });
}

/**
 * Fetch single guidance by topic ID
 */
export async function fetchGuidanceByTopic(topic: string): Promise<SanityGuidance | null> {
  if (!isSanityConfigured) {
    console.warn('Sanity not configured, returning null');
    return null;
  }
  return sanityClient.fetch(guidanceByTopicQuery, { topic });
}

/**
 * Fetch all guidance content
 */
export async function fetchAllGuidance(): Promise<SanityGuidance[]> {
  if (!isSanityConfigured) {
    console.warn('Sanity not configured, returning empty array');
    return [];
  }
  return sanityClient.fetch(allGuidanceQuery);
}

/**
 * Fetch guidance grouped by category
 */
export async function fetchGuidanceByCategory(): Promise<Record<string, SanityGuidance[]>> {
  if (!isSanityConfigured) {
    console.warn('Sanity not configured, returning empty object');
    return { values: [], habits: [], routines: [], review: [] };
  }
  return sanityClient.fetch(guidanceByCategoryQuery);
}

